<template>
  <v-col>
    <Loader v-show="loading" :loading="loading" />
    <PrintForm
      v-if="printMode"
      :document="document"
      class="invisible-block print"
      @close="changePrintMode"
    />
    <v-card class="no-print">
      <v-card-title class="pb-0">
        <v-row no-gutters align="start">
          <v-col>
            <span v-if="isNew" class="headline font-weight-medium">{{
              header
            }}</span>
            <div v-else>
              <v-hover
                v-if="!titleEditMode && !isNew && title.length"
                v-slot="{ hover }"
              >
                <div
                  :elevation="hover ? 12 : 2"
                  :class="{ 'on-hover': hover, 'hover-block': true }"
                >
                  <span class="title font-weight-bold">{{ title }}</span>
                  <HoverBtn
                    :hover="hover"
                    class="pb-1"
                    @handler="titleEditMode = true"
                  />
                </div>
              </v-hover>
            </div>
          </v-col>
          <v-col md="2" sm="2" cols="3" align="end">
            <IconBtn
              v-if="!modal && !isNew"
              icons="print"
              :large="true"
              color="grey darken-4"
              @click="changePrintMode"
            />
            <IconBtn
              v-if="!modal"
              icons="close"
              :large="true"
              color="grey darken-4"
              @click="
                () => {
                  $router.go(-1);
                }
              "
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <TitleEditor
          v-if="titleEditMode || isNew"
          v-model="title"
          label="Найменування документу*"
          :edit="!isNew"
          :error-messages="$v.title.$error ? errors.TITLE : []"
          :hide-details="false"
          @input="isChanged = true"
          @apply="updateTitle"
          @close="closeEditTitleMode"
        />
        <v-hover v-if="!tagEditMode && !isNew" v-slot="{ hover }">
          <div
            :elevation="hover ? 12 : 2"
            :class="{ 'on-hover': hover, 'hover-block': true }"
            class="mb-2"
          >
            <ChipsTag
              v-for="tag in tags"
              :key="tag.id"
              :value="tag"
              :close="false"
              @update="updateTag"
              @delete="deleteTag"
            />
            <span v-if="document && !document.tags.length">Додати теги</span>
            <HoverBtn :hover="hover" @handler="tagEditMode = true" />
          </div>
        </v-hover>
        <SelectTags
          v-else
          v-model="tags"
          :multiple-select="true"
          :add-btn="true"
          :edit="!isNew"
          label="Теги*"
          :error-messages="$v.tags.$error ? errors.TAGS : []"
          :hide-details="false"
          class="py-0"
          @input="isChanged = true"
          @edit="updateTags"
          @close="closeEditTagMode"
          @add="addTags"
        />
        <v-row v-if="isNew || !blocks.length" align="center">
          <v-col cols="10" class="py-0">
            <SelectTags
              v-model="tagsForDocument"
              :multiple-select="true"
              :edit="false"
              :append="true"
              label="Теги для пошуку блоків"
              component-name="DropdownMenu"
              class="pt-0 pb-3"
              @add-block="addDocumentBlock"
            />
          </v-col>
          <v-col cols="2" class="pa-0">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <IconBtn icons="add" v-on="on" @click="openModal(false)" />
              </template>
              <span>{{ tooltip }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <DragnDrop
          :value="blocks"
          component-name="DocumentBlockViewer"
          :items="blockVersions"
          @edit="openModal"
          @delete="confirm"
          @add="addDocumentBlock"
          @move="moveBlock"
          @apply="applyNewVersion"
          @decline="declineNewVersion"
        />
        <ModalWindow
          :id="editableBlock ? editableBlock.blockId : '0'"
          v-model="isModal"
          component-name="DocumentBlock"
          @add="addDocumentBlock"
          @update="applyNewVersion"
          @input="closeModal"
        />
      </v-card-text>
      <v-card-actions v-if="isNew">
        <PrimaryBtn
          :loading="loading"
          :has-warnings="$v.$invalid"
          :disabled="!isChanged || !isNew"
          @click="save"
        />
      </v-card-actions>
    </v-card>
  </v-col>
</template>
<script>
import SelectTags from "../tags/SelectTags.vue";
import PrimaryBtn from "../../components/PrimaryBtn.vue";
import IconBtn from "../../components/IconBtn.vue";
import ModalWindow from "../../components/ModalWindow.vue";
import ChipsTag from "../tags/ChipsTag.vue";
import TitleEditor from "../../components/TitleEditor.vue";
import HoverBtn from "../../components/HoverBtn.vue";
import DragnDrop from "../../components/DragnDrop.vue";
import Loader from "../../components/Loader.vue";
import PrintForm from "./PrintForm.vue";
import * as documentsApi from "../../backend/documentsApi.js";
import { getDocumentBlock } from "../../backend/document-blocksApi.js";

import { ERRORS } from "../../helpers";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    SelectTags,
    PrimaryBtn,
    IconBtn,
    ModalWindow,
    ChipsTag,
    TitleEditor,
    HoverBtn,
    DragnDrop,
    Loader,
    PrintForm,
  },
  props: ["id", "modal"],
  data: () => ({
    title: "",
    tags: [],
    blocks: [],
    document: null,
    loading: false,
    isChanged: false,
    isModal: false,
    tooltip: "Створити блок",
    tagEditMode: false,
    editableBlock: null,
    titleEditMode: false,
    blockVersions: [],
    printMode: false,
    header: "Створення документу",
    tagsForDocument: [],
  }),

  validations: {
    title: {
      required,
    },
    tags: {
      required,
    },
  },

  computed: {
    isNew() {
      return this.id != 0 ? false : true;
    },
    errors() {
      return {
        TITLE: ERRORS.REQUIRED,
        TAGS: ERRORS.REQUIRED,
      };
    },
  },
  created() {
    if (!this.isNew) {
      this.getDocument();
    }
  },
  methods: {
    async getDocument() {
      const doc = await documentsApi.getDocument(this.id);
      this.document = doc;
      this.title = doc.title;
      this.tags = doc.tags;
      this.blocks = doc.blocks;
    },
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$notifyWarning();
        return;
      }

      this.loading = true;
      const document = {
        title: this.title,
        tags: [
          ...new Set(
            this.tags.map((dt) => dt.id).concat(this.tags.map((t) => t.id))
          ),
        ],
        blocks: this.blocks.map((block, index) => ({
          id: block.id,
          order: index + 1,
        })),
      };
      try {
        await documentsApi.createDocument(document);
        this.$notifySuccess();
        this.$router.go(-1);
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    updateTag(tag) {
      this.tags.forEach((t) => {
        if (t.id !== tag.id) {
          return;
        }
        t.title = tag.title;
      });
      this.$notifySuccess();
    },
    deleteTag(tag) {
      const index = this.tags.findIndex((t) => t.id === tag.id);
      this.tags.splice(index, 1);
      this.$notifySuccess();
    },
    async updateTitle(text) {
      this.loading = true;
      this.title = text;
      this.document.title = text;
      await documentsApi.updateDocumentsTitle(this.id, text);
      this.titleEditMode = false;
      this.isChanged = false;
      this.loading = false;
      this.$notifySuccess();
    },
    closeEditTitleMode() {
      this.title = this.document.title;
      this.titleEditMode = false;
    },
    async updateTags() {
      this.loading = true;
      await documentsApi.updateDocumentsTags(this.id, [
        ...new Set(this.tags.map((t) => t.id)),
      ]);
      this.document.tags = this.tags;
      this.tagEditMode = false;
      this.isChanged = true;
      this.loading = false;
      this.$notifySuccess();
    },
    closeEditTagMode() {
      this.tags = this.document.tags;
      this.tagEditMode = false;
    },
    async addDocumentBlock(block) {
      this.loading = true;
      try {
        if (this.isNew && block.blockId) {
          const newBlock = await getDocumentBlock(block.blockId);
          this.blocks.splice(block.order, 0, newBlock);
        } else {
          if (block.blockId) {
            const blockForSaving = {
              id: this.id,
              blockId: block.blockId,
              order: block.order + 1 || this.editableBlock.order + 1 || 1,
            };

            await documentsApi.addDocumentsBlock(
              blockForSaving.id,
              blockForSaving.blockId,
              blockForSaving.order
            );
          }
          this.$notifySuccess();
          if (!this.isNew) {
            await this.getDocument();
          }
        }
      } catch (err) {
        this.$notifyWarning(err.response.data);
      } finally {
        this.isModal = false;
        this.editableBlock = null;
        this.isChanged = true;
        this.loading = false;
      }
    },
    openModal(block) {
      this.editableBlock = block ? { ...block } : 0;
      this.isModal = true;
    },
    addTags(values) {
      this.tags = [...this.tags, ...values];
      this.isChanged = true;
      this.$notifySuccess();
    },
    async confirm(blockId) {
      if (this.isNew) {
        const index = this.blocks.findIndex((b) => b.id === blockId);
        this.blocks.splice(index, 1);
      } else {
        await documentsApi.deleteDocumentBlock(this.id, blockId);
        this.getDocument();
        this.$notifySuccess();
      }
    },
    async moveBlock(items) {
      this.blocks = items;
      this.document ? (this.document.blocks = this.blocks) : false;
      if (!this.isNew) {
        this.loading = true;
        await documentsApi.updateOrderDocumentsBlocks(this.id, [
          ...new Set(
            this.blocks.map((b) => {
              return { id: b.id, order: b.order };
            })
          ),
        ]);
        this.loading = false;
        this.$notifySuccess();
      }
    },
    closeModal() {
      this.editableBlock = null;
      this.isModal = false;
    },
    async applyNewVersion(block) {
      this.loading = true;
      if (this.isNew) {
        const newBlock = await getDocumentBlock(block.versionId);
        const index = this.blocks.findIndex((b) => b.id === block.id);
        this.blocks.splice(index, 1, newBlock);
      } else {
        await documentsApi.updateDocumentBlockVersion(
          this.id,
          block.id,
          block.versionId
        );
        this.getDocument();
      }
      this.isModal = false;
      this.loading = false;
      this.$notifySuccess();
    },
    async declineNewVersion(block) {
      this.loading = true;
      await documentsApi.updateDocumentBlockVersion(
        this.id,
        block.item.id,
        null
      );
      this.getDocument();
      this.loading = false;
      this.$notifySuccess();
    },
    changePrintMode(value) {
      this.printMode = value;
    },
  },
};
</script>

<style lang="scss">
.document-block {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;
      word-wrap: break-word;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    p {
      margin: 0;
    }
  }
}

.resize-cursor {
  cursor: row-resize;
}
</style>
